import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { SearchForm } from '@/components/generals/search-form'
import { mainNav } from '@/constants/site'

interface SiteHeaderProps {
    overlay?: boolean
}

const useIsActive = () => {
    const router = useRouter()
    const device = typeof router.query.device === 'string' ? router.query.device : ''

    return (href: string): boolean => {
        if (href === '/categories') return router.pathname.startsWith('/categories')
        if (href === '/wallpapers?device=desktop') return router.pathname === '/wallpapers' && device === 'desktop'
        if (href === '/wallpapers?device=phone') return router.pathname === '/wallpapers' && device === 'phone'
        if (href === '/wallpapers') return router.pathname.startsWith('/wallpapers') && device !== 'desktop' && device !== 'phone'

        return router.pathname === href
    }
}

export const SiteHeader: FC<SiteHeaderProps> = ({ overlay = false }): ReactNode => {
    const router = useRouter()
    const isActive = useIsActive()
    const [menuOpen, setMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const currentQuery = router.pathname === '/wallpapers' && typeof router.query.q === 'string' ? router.query.q : ''

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [menuOpen])

    const solid = !overlay || scrolled || menuOpen

    return (
        <header
            className={cn(
                'inset-x-0 top-0 z-50 transition-colors duration-300',
                overlay ? 'fixed' : 'sticky',
                solid ? 'border-b border-white/[0.06] bg-background/80 backdrop-blur-xl' : 'border-b border-transparent bg-transparent'
            )}
        >
            <Container className='flex h-16 items-center gap-6'>
                <Link
                    href='/'
                    className='rounded-lg focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                >
                    <Logo />
                    <span className='sr-only'>Home</span>
                </Link>

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {mainNav.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className={cn(
                                        'inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                        isActive(link.href) ? 'bg-white/[0.08] text-foreground' : 'text-foreground/70 hover:bg-white/[0.05] hover:text-foreground'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='ml-auto hidden w-full max-w-xs lg:block'>
                    <SearchForm defaultValue={currentQuery} />
                </div>

                <button
                    type='button'
                    onClick={() => setMenuOpen(open => !open)}
                    aria-expanded={menuOpen}
                    aria-controls='mobile-menu'
                    className='ml-auto inline-flex size-10 cursor-pointer items-center justify-center rounded-xl text-foreground transition hover:bg-white/[0.08] focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none lg:hidden'
                >
                    {menuOpen ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}
                    <span className='sr-only'>{menuOpen ? 'Close menu' : 'Open menu'}</span>
                </button>
            </Container>

            <div
                id='mobile-menu'
                hidden={!menuOpen}
                className='border-t border-white/[0.06] bg-background/95 backdrop-blur-xl lg:hidden'
            >
                <Container className='space-y-4 py-5'>
                    <SearchForm defaultValue={currentQuery} onSubmitted={() => setMenuOpen(false)} />

                    <nav aria-label='Mobile' className='md:hidden'>
                        <ul className='grid grid-cols-2 gap-2'>
                            {mainNav.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={isActive(link.href) ? 'page' : undefined}
                                        className={cn(
                                            'flex h-12 items-center rounded-xl border px-4 text-sm font-medium transition-colors',
                                            isActive(link.href) ? 'border-white/20 bg-white/[0.08] text-foreground' : 'border-white/[0.08] text-foreground/80 hover:bg-white/[0.05]'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </Container>
            </div>
        </header>
    )
}
